import React from 'react';
interface TypeBreakdown {
  label: string;
  count: number;
  subtotal: number;
  discountPercent: number;
  onDiscountChange: (percent: number) => void;
  discountAmount: number;
  totalDue: number;
  warnings: string[];
}
interface PricingSummaryProps {
  productLine: string;
  totalItemCount: number;
  totalBasePrice: number;
  totalAdderCost: number;
  grandTotal: number;
  totalDue: number;
  types: TypeBreakdown[];
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
export function PricingSummary({
  productLine,
  totalItemCount,
  totalBasePrice,
  totalAdderCost,
  grandTotal,
  totalDue,
  types
}: PricingSummaryProps) {
  const activeTypes = types.filter(t => t.count > 0);
  return <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-82">
      <div className="bg-[#2E5E3F] px-6 py-3" data-id="element-83">
        <h2 className="text-white font-semibold text-sm uppercase tracking-wide" data-id="element-84">
          Quote Summary
        </h2>
      </div>
      <div className="px-6 py-5" data-id="element-85">
        {/* Top-level stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-86">
          <div data-id="element-87">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1" data-id="element-88">
              Product Line
            </p>
            <p className="text-sm font-semibold text-gray-900" data-id="element-89">{productLine}</p>
          </div>
          <div data-id="element-90">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1" data-id="element-91">
              Total Items
            </p>
            <p className="text-sm font-semibold text-gray-900" data-id="element-92">
              {totalItemCount}
            </p>
          </div>
          <div data-id="element-93">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1" data-id="element-94">
              Base Total
            </p>
            <p className="text-sm font-semibold text-gray-900 tabular-nums" data-id="element-95">
              {formatCurrency(totalBasePrice)}
            </p>
          </div>
          <div data-id="element-96">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1" data-id="element-97">
              Adders Total
            </p>
            <p className="text-sm font-semibold text-gray-900 tabular-nums" data-id="element-98">
              {formatCurrency(totalAdderCost)}
            </p>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-gray-200 flex items-center justify-between" data-id="element-99">
          <span className="text-sm font-medium text-gray-600" data-id="element-100">Subtotal</span>
          <span className="text-lg font-semibold text-gray-900 tabular-nums" data-id="element-101">
            {formatCurrency(grandTotal)}
          </span>
        </div>

        {/* Per-type discount sections */}
        {activeTypes.map(type => <div key={type.label} className="mt-4 pt-4 border-t border-gray-100" data-id="element-102">
            <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-103">
              <div className="flex items-center gap-3" data-id="element-104">
                <span className="text-sm font-medium text-gray-700 min-w-[120px]" data-id="element-105">
                  {type.label}
                  <span className="text-xs text-gray-400 ml-1.5" data-id="element-106">
                    ({type.count} item{type.count !== 1 ? 's' : ''} ·{' '}
                    {formatCurrency(type.subtotal)})
                  </span>
                </span>
                <div className="flex items-center gap-2" data-id="element-107">
                  <label htmlFor={`discount-${type.label}`} className="text-sm text-gray-500" data-id="element-108">
                    Discount
                  </label>
                  <div className="relative" data-id="element-109">
                    <input id={`discount-${type.label}`} type="number" min={0} max={100} step={0.5} value={type.discountPercent || ''} onChange={e => {
                  const val = Number(e.target.value);
                  type.onDiscountChange(Math.min(Math.max(val, 0), 100));
                }} placeholder="0" className="w-20 px-2.5 py-1.5 text-sm text-right border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 tabular-nums pr-7" data-id="element-110" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-400" data-id="element-111">
                      %
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-112">
                {type.discountAmount > 0 && <span className="text-sm font-medium text-red-600 tabular-nums" data-id="element-113">
                    −{formatCurrency(type.discountAmount)}
                  </span>}
                <span className="text-sm font-semibold text-gray-900 tabular-nums" data-id="element-114">
                  {formatCurrency(type.totalDue)}
                </span>
              </div>
            </div>
            {type.warnings.length > 0 && <div className="mt-2 space-y-1" data-id="element-115">
                {type.warnings.map((warning, i) => <p key={i} className="text-xs font-medium text-red-500 flex items-center gap-1.5" data-id="element-116">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" data-id="element-117" />
                    {warning}
                  </p>)}
              </div>}
          </div>)}

        {/* Combined Total Due */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between" data-id="element-118">
          <span className="text-base font-semibold text-gray-800" data-id="element-119">
            Total Due
          </span>
          <span className="text-2xl font-bold text-[#2E5E3F] tabular-nums" data-id="element-120">
            {formatCurrency(totalDue)}
          </span>
        </div>
      </div>
    </div>;
}